import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Title, LaneHeader } from '../common/board/styles/Base';
import InlineInputController from '../common/board/widgets/InlineInput';
import MenuActionsPopupMenu from './menu_actions_popup_menu';
import AnalyzeAllMealsConfirmationPopup from './analyze_all_meals_confirmation_popup';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  scaleMealIngredientsOperation,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import {
  useGetSharedProgrammesQuery,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullMealsByParentQuery,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { userIsCreator } from '../../services/users';
import { sharedProgrammesWhichReferenceMeals } from '../../services/programmes';
import { mealWithScaledIngredientsReplaced } from '../../services/meals';

const MenuHeader = ({
  id,
  appBoardId: recipesBoardId,
  onDoubleClick,
  onDelete,
  editLaneTitle,
  title,
  titleStyle,
  laneDraggable,
  updateTitle,
}) => {
  const dispatch = useDispatch();
  const setGlobalSnackbar = (obj) => dispatch(showGlobalSnackbar(obj));

  const [analyzePopupAnchorEl, setAnalyzePopupAnchorEl] = useState(null);

  const [analyzeProgress, setAnalyzeProgress] = useState(null);

  // console.log(`MenuHeader recipesBoardId ${recipesBoardId}`);

  const isBoardReadOnly = !!titleStyle?._isBoardReadOnly;

  const onShowAnalyzeAllMeals = (ev) => {
    setAnalyzePopupAnchorEl(ev.target);
  };

  const { data: recipesBoard } = useGetSmorgBoardQuery(recipesBoardId, {
    skip: !recipesBoardId,
  });

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const { data: healthProSpace } = useGetSpaceQuery();

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );
  const menu = recipesBoard.menus.find((m) => m.id === id);
  if (!menu) {
    console.warn(`Could not find menu ${id}`);
  }
  const { mealIDs } = menu || {};

  const sharedProgrammesLinked = sharedProgrammesWhichReferenceMeals(
    mealIDs,
    sharedProgrammes || [],
  );

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [updateMeal] = useUpdateMealMutation();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const onDoAnalyzeAllMeals = useCallback(async () => {
    setAnalyzeProgress(0);
    try {
      if (!recipesBoard) {
        console.warn(`Could not find recipes board ${recipesBoardId}`);
        return Promise.reject();
      }
      const { data: fullMeals } = await lazyGetFullMealsByParent(
        recipesBoardId,
      );
      if (!mealIDs) {
        return Promise.reject();
      }
      const promises = mealIDs.map(async (mealID) =>
        updateFoodBrainDerivedDataOnMeal(
          fullMeals[mealID],
          customerFoodDataID,
          updateMeal,
        ),
      );
      await Promise.all(promises);
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Meals have been analyzed',
        }),
      );
      return true;
    } finally {
      setAnalyzeProgress(null);
      setAnalyzePopupAnchorEl(null);
    }
  }, [
    customerFoodDataID,
    dispatch,
    lazyGetFullMealsByParent,
    mealIDs,
    recipesBoard,
    recipesBoardId,
    updateMeal,
  ]);

  const onImportMealsFromCSV = (/* fileList */) => {
    // TODO
    // dispatch(importMealsFromCSVAction(fileList, recipesBoardId, id));
  };

  return (
    <LaneHeader onDoubleClick={onDoubleClick} editLaneTitle={editLaneTitle}>
      <div>
        <Title draggable={laneDraggable}>
          {editLaneTitle ? (
            <InlineInputController
              value={title}
              border
              placeholder="My placeholder"
              resize="vertical"
              onSave={updateTitle}
            />
          ) : (
            title
          )}
        </Title>
      </div>
      {!isBoardReadOnly && (
        <MenuActionsPopupMenu
          recipesBoardId={recipesBoardId}
          sharedProgrammesLinked={sharedProgrammesLinked}
          onAddMenuToMealsBasket={async (scaleToServings) => {
            const mealIDsToAddToBasket = mealIDs.filter(Boolean);
            const fullMealsToAddToBasket = await Promise.all(
              mealIDsToAddToBasket.map(async (mealID) => {
                const { data: fullMeal } = await lazyGetMeal(mealID);
                return fullMeal;
              }),
            );
            const scaledMealsToAddToBasket = await Promise.all(
              fullMealsToAddToBasket.map(async (fullMeal) => {
                if (!scaleToServings) {
                  return fullMeal;
                }
                const scaleFactor =
                  scaleToServings / (fullMeal.recipes[0]?.servings || 1);
                const scaledIngredients = await scaleMealIngredientsOperation(
                  fullMeal,
                  scaleFactor,
                  customerFoodDataID,
                );
                return mealWithScaledIngredientsReplaced(
                  fullMeal,
                  scaleToServings,
                  scaledIngredients,
                  scaleFactor,
                );
              }),
            );
            dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
          }}
          onCopyToRecipesBoard={
            (/* toRecipesBoardId */) =>
              // dispatch(
              //   menuCopiedToRecipesBoardAction(
              //     recipesBoardId,
              //     id,
              //     toRecipesBoardId,
              //   ),
              // )
              {}
          }
          setGlobalSnackbar={setGlobalSnackbar}
          onDeleteMenu={onDelete}
          isBoardReadOnly={isBoardReadOnly}
          onAnalyzeAllMeals={onShowAnalyzeAllMeals}
          onImportMealsFromCSV={onImportMealsFromCSV}
        />
      )}
      {analyzePopupAnchorEl && (
        <AnalyzeAllMealsConfirmationPopup
          anchorEl={analyzePopupAnchorEl}
          onDismiss={() => setAnalyzePopupAnchorEl(null)}
          onConfirm={onDoAnalyzeAllMeals}
          analyzeProgress={analyzeProgress}
        />
      )}
    </LaneHeader>
  );
};

MenuHeader.propTypes = {
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  editLaneTitle: PropTypes.bool.isRequired,
  laneDraggable: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  titleStyle: PropTypes.object.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  updateTitle: PropTypes.func.isRequired,
};

export default MenuHeader;
